/**
 * CAMPAIGNS. What a batch of letters or an ad flight was meant to cost, what the spend naming it
 * came to, and which households its word brought in inside its own days (ops #5).
 *
 * `docs/erd.md` Part 5B. A campaign is a cost, a date range and a word, and IT IS NOT A SEND
 * ENGINE: nothing on this screen composes, schedules or sends anything, and there is no endpoint
 * behind it that could. Every message leaves through the phone system and nowhere else.
 *
 * THE FIGURES ARE THE SERVER'S. `api/src/reports/campaigns.ts` counts the enquiries and households
 * carrying the campaign's word on its channel inside its days and the fourteen after them, in
 * Florida, and sums the live spend that names it. The word is the campaign word the website read
 * off the visit, a QR code's or a link's, or the source detail the office typed, exactly (ops #47).
 * This screen draws what it is sent and divides nothing. A campaign nobody came from has no cost
 * per household, and that is a dash and never $0.00: a cost of nothing is a claim, and a dash is the
 * absence of one. The word seen on another channel is said beside the campaign in a sentence and
 * never added to its figures, because the server never adds it either.
 *
 * IT IS A WORD IN THE RAIL BESIDE SOURCES, because it is the same question asked of one batch
 * rather than one channel, at the same desk with the plan open, and never between two houses.
 *
 * THE WORD IS THE ATTRIBUTION, so the form says so where the box is. A word typed here that nobody
 * types on an enquiry counts nobody, and the list will say nobody rather than guess.
 *
 * NO PRICE, NO RATE AND NOTHING A CLIENT PAYS IS TYPED HERE. The one figure is what the office meant
 * to spend on the campaign, which is money going out.
 *
 * A CAMPAIGN GROWTH FILED IS GROWTH'S (ops #47, `docs/seams/growth.md` section 6). Growth sends the
 * whole campaign again whenever it moves, so a correction typed here would be overwritten with nobody
 * told: such a line says "From growth", opens on its notes alone, and has no discard. The server
 * refuses anything else whatever this screen offers; this is the screen saying so first.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CAMPAIGN_STATUSES, CHANNELS, type CampaignStatus, type Channel } from '@fk/shared';
import {
  ApiError,
  createCampaign,
  discardCampaign,
  getCampaignsReport,
  patchCampaign,
  type CampaignLine,
  type CampaignsReport,
  type Discard,
  type Me,
  type NewCampaign,
} from '../api.ts';
import { Card, MEASURE, Pill, ScreenTitle } from '../components/AppShell.tsx';
import { DiscardControl } from '../components/Discard.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import {
  DataTable,
  PanelRule,
  SidePanel,
  SplitLayout,
  useWideView,
  type DataColumn,
} from '../components/ListView.tsx';
import { Busy, EmptyNotice, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import {
  centsFromDollars,
  dollarsForInput,
  formatDay,
  isDay,
  money,
  orNull,
  shortDay,
} from '../format.ts';
import { CAMPAIGN_STATUS_LABELS, CHANNEL_LABELS } from '../labels.ts';
import { NOTHING_TO_SAY, rollUpBesideTheTable, rollUpUnderTheCard } from '../reports.ts';

/** What a person has typed, before any of it is a campaign. Dollars stay a string until they parse. */
export interface CampaignTyped {
  readonly name: string;
  readonly channel: Channel | '';
  readonly platform: string;
  readonly dollars: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly sourceDetail: string;
  readonly status: CampaignStatus;
  readonly notes: string;
}

/** A fresh form: the server's own day as the first day, planned, and no end decided. */
export function emptyCampaign(today: string): CampaignTyped {
  return {
    name: '',
    channel: '',
    platform: '',
    dollars: '',
    startsOn: today,
    endsOn: '',
    sourceDetail: '',
    status: 'planned',
    notes: '',
  };
}

/** The campaign as it stands, so a correction opens on what is there rather than on an empty form. */
function typedFrom(line: CampaignLine): CampaignTyped {
  return {
    name: line.name,
    channel: line.channel,
    platform: line.platform ?? '',
    dollars: dollarsForInput(line.plannedBudgetCents),
    startsOn: line.startsOn,
    endsOn: line.endsOn ?? '',
    sourceDetail: line.sourceDetail,
    status: line.status,
    notes: line.notes ?? '',
  };
}

/** What the endpoints take, or the sentence saying which box is wrong. Never both. */
export type CampaignChecked =
  | { readonly ok: true; readonly body: NewCampaign }
  | { readonly ok: false; readonly problem: string };

/**
 * Every refusal the server would make, made here first and in a sentence naming the box. The
 * server refuses all of them again, and the server is the one that counts. The same body goes to
 * both doors, so a correction is checked as the whole campaign it will leave behind: moving only
 * the first day past the last is caught here as surely as typing the pair backwards.
 */
export function checkCampaign(typed: CampaignTyped): CampaignChecked {
  if (!typed.name.trim()) {
    return { ok: false, problem: 'Name the campaign, the way the office will look for it.' };
  }
  if (typed.channel === '') return { ok: false, problem: 'Choose the channel this campaign buys.' };
  if (!typed.sourceDetail.trim()) {
    return {
      ok: false,
      problem: 'Type the word its enquiries are tagged with. Without one it counts nobody.',
    };
  }
  const plannedBudgetCents = centsFromDollars(typed.dollars);
  if (plannedBudgetCents === null) {
    return {
      ok: false,
      problem: 'Type the planned budget in dollars, like 450 or 450.00, or 0 if none is planned.',
    };
  }
  if (!isDay(typed.startsOn)) return { ok: false, problem: 'Pick the first day of the campaign.' };
  if (typed.endsOn !== '' && !isDay(typed.endsOn)) {
    return {
      ok: false,
      problem: 'The last day is not a day. Leave it empty while nobody has decided.',
    };
  }
  if (typed.endsOn !== '' && typed.endsOn < typed.startsOn) {
    return {
      ok: false,
      problem:
        'It ends before it starts. Pick an end on or after the first day, or leave it empty.',
    };
  }
  return {
    ok: true,
    body: {
      name: typed.name.trim(),
      channel: typed.channel,
      platform: orNull(typed.platform),
      plannedBudgetCents,
      startsOn: typed.startsOn,
      endsOn: orNull(typed.endsOn),
      sourceDetail: typed.sourceDetail.trim(),
      status: typed.status,
      notes: orNull(typed.notes),
    },
  };
}

/** The cost per household in dollars, and the report's own dash where nobody came. */
export function perHouseholdWords(cents: number | null): string {
  return cents === null ? NOTHING_TO_SAY : money(cents);
}

/** The days the figures were counted over, both ends included, or none before it starts. */
export function countedWords(
  counted: { readonly from: string; readonly to: string } | null,
): string {
  if (counted === null) return 'Not started, so nothing is counted yet.';
  return `Counted ${formatDay(counted.from) ?? counted.from} to ${formatDay(counted.to) ?? counted.to}.`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * WHAT ITS WORD BROUGHT IN ON ANOTHER CHANNEL, said once beside the campaign and never added to it:
 * a channel is what a kill rule counts, so a word on the wrong one is a disagreement for a person
 * to see. Nothing is said where there is nothing to say.
 */
export function elsewhereWords(
  line: Pick<CampaignLine, 'leadsElsewhere' | 'householdsElsewhere'>,
): string | null {
  if (line.leadsElsewhere === 0 && line.householdsElsewhere === 0) return null;
  return `Its word also came in on another channel: ${plural(line.leadsElsewhere, 'lead', 'leads')} and ${plural(line.householdsElsewhere, 'household', 'households')}, not counted here.`;
}

/**
 * WHAT ITS PEOPLE CAME TO (ops #47, step 3), the figures growth's outcomes read carries, computed by
 * the same functions on the server: of its leads the booked, of its households the recurring and
 * the ended, what its households paid, and what each recurring household cost. The money paid is
 * never divided by the households here either, because it is drawn from all of them, and a campaign
 * nobody recurred from has no cost per recurring household rather than a cost of nothing. Where
 * refunds and corrections outweigh what was paid, the money is said as handed back, never as a
 * payment below nothing.
 */
export function outcomeWords(
  line: Pick<
    CampaignLine,
    'booked' | 'recurring' | 'ended' | 'revenueCents' | 'costPerRecurringHouseholdCents'
  >,
): string {
  const each =
    line.costPerRecurringHouseholdCents === null
      ? 'no household recurring yet'
      : `${money(line.costPerRecurringHouseholdCents)} for each that recurs`;
  const paid =
    line.revenueCents < 0
      ? `its households have had ${money(-line.revenueCents)} more handed back than they paid, tips out`
      : `its households have paid ${money(line.revenueCents)}, refunds off and tips out`;
  return `${line.booked} booked, ${line.recurring} recurring, ${line.ended} ended; ${paid}; ${each}.`;
}

export function Campaigns({ me }: { me: Me }) {
  const [report, setReport] = useState<CampaignsReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [opened, setOpened] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const wide = useWideView();

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    getCampaignsReport()
      .then((given) => {
        if (live) setReport(given);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The campaigns did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  /** Said once, then cleared by the next thing anybody does, the way every other screen does it. */
  const done = (sentence: string) => {
    setSaid(sentence);
    setOpened(null);
    reload();
  };

  const rows = report?.campaigns ?? [];
  const openedRow = rows.find((row) => row.id === opened) ?? null;
  const beside = rollUpBesideTheTable(wide, openedRow !== null) ? openedRow : null;

  return (
    <div>
      <ScreenTitle
        title="Campaigns"
        subtitle="What a batch of letters or an ad flight cost, and who came from it."
      />

      <p className="mb-4 flex flex-wrap gap-x-5 text-sm">
        <Link
          to="/sources"
          className="inline-flex min-h-11 desk:min-h-9 items-center text-navy/70 underline"
        >
          Where households came from
        </Link>
        <Link
          to="/spend"
          className="inline-flex min-h-11 desk:min-h-9 items-center text-navy/70 underline"
        >
          What the business spent
        </Link>
      </p>

      <AddOne today={me.today} onSaved={done} />

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      {error ? (
        <ErrorNotice title="The campaigns did not load" message={error.message} onRetry={reload} />
      ) : report === null ? (
        <Busy what="the campaigns" />
      ) : rows.length === 0 ? (
        <EmptyNotice title="No campaigns yet">
          <p>
            Add one above: a name, the channel it buys, the word its enquiries will carry, a budget
            and a first day. Its figures appear here as households arrive with that word.
          </p>
        </EmptyNotice>
      ) : (
        <SplitLayout
          panel={
            beside === null ? undefined : (
              <OpenedCampaign
                key={beside.id}
                line={beside}
                onClose={() => setOpened(null)}
                onSaved={done}
              />
            )
          }
        >
          <p className={`mb-2 text-xs text-navy/60 ${MEASURE}`}>
            {plural(rows.length, 'campaign', 'campaigns')}, the latest first day first. Press one to
            correct it.
          </p>

          <DataTable
            label="Campaigns"
            groups={[{ key: 'campaigns', rows }]}
            columns={campaignColumns(setOpened)}
            rowKey={(row) => row.id}
            rowClassName={(row) =>
              row.id === opened
                ? 'bg-gold/10 [&>td:first-child]:border-l-[3px] [&>td:first-child]:border-l-gold [&>td:first-child]:pl-[13px]'
                : ''
            }
            card={(row) => (
              <>
                <CampaignCard
                  line={row}
                  opened={row.id === opened}
                  onOpen={() => setOpened(row.id)}
                />
                {/*
                 * The rule Spend and Sources use, and the same two functions: below md the split
                 * lays its panel out after the whole list, so a correction opened from a card
                 * would be drawn a long way under the finger that asked for it.
                 */}
                {rollUpUnderTheCard(wide, row.id === opened) ? (
                  <div className="mt-3">
                    <OpenedCampaign line={row} onClose={() => setOpened(null)} onSaved={done} />
                  </div>
                ) : null}
              </>
            )}
          />

          {rows.length >= report.limit ? (
            <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
              This is the first {report.limit} campaigns, which is the most one read answers with.
            </p>
          ) : null}

          <p className={`mt-3 text-xs text-navy/60 ${MEASURE}`}>
            Leads and households are the enquiries and clients on a campaign&apos;s channel carrying
            its word, from its first day to fourteen days after its last, counted in Florida. The
            word is the one on its links and QR codes, or exactly the source detail typed. Spent is
            every expense that names the campaign, whatever its day. Booked, recurring and ended are
            of those leads and households, and paid is everything its households have paid, refunds
            taken off and tips left out.
          </p>
        </SplitLayout>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The list, in two shapes. Below md it is not a table at all.
 * ------------------------------------------------------------------ */

/**
 * Sage while it runs, gold while it rests, as a paused household and a paused agreement are: a
 * campaign switched off for a while is one somebody is coming back to (ops #49). Planned and ended
 * are quiet.
 */
function statusTone(status: CampaignStatus): 'quiet' | 'sage' | 'gold' {
  if (status === 'running') return 'sage';
  return status === 'paused' ? 'gold' : 'quiet';
}

/** What rides under a campaign's name: the channel it buys and the word it counts by. */
export function campaignWord(line: Pick<CampaignLine, 'channel' | 'sourceDetail'>): string {
  return `${CHANNEL_LABELS[line.channel]}: ${line.sourceDetail}`;
}

/**
 * A campaign on one line, for a list of choices where the word cannot ride underneath: its name,
 * then its word, so two batches given one name are still told apart. The spend screen offers
 * campaigns in these words, which is why they are written once, here, beside the list they copy.
 */
export function campaignLabel(
  line: Pick<CampaignLine, 'name' | 'channel' | 'sourceDetail'>,
): string {
  return `${line.name} (${campaignWord(line)})`;
}

/** "Tue, Sep 1 to Sat, Oct 31", or "From Tue, Sep 1" while nobody has decided when it ends. */
function shortDays(line: CampaignLine): string {
  const from = shortDay(line.startsOn);
  return line.endsOn === null ? `From ${from}` : `${from} to ${shortDay(line.endsOn)}`;
}

/**
 * THE WIDTHS ARE SHARES, NOT PIXELS, for the reason `screens/Money.tsx` sets out: pixel widths
 * that add up to more than the room the rail leaves push the last columns off an edge. The word
 * rides under the name rather than taking a column, because a campaign and its word are one fact,
 * and the last three cells are bare figures so the eye can run down them.
 *
 * THE NAME WRAPS AND THE PILL KEEPS ITS ROOM (ops #56). At 768 the name was cut to "Nextdoor
 * Lan..." and the status pill ran out of its cell, so the name is never cut and the two columns
 * name the least they are read at: below that the table scrolls in its frame.
 */
function campaignColumns(open: (id: string) => void): readonly DataColumn<CampaignLine>[] {
  return [
    {
      key: 'campaign',
      header: 'Campaign',
      width: '24%',
      minWidth: '144px',
      cell: (row) => {
        const elsewhere = elsewhereWords(row);
        return (
          <button
            type="button"
            onClick={() => open(row.id)}
            className="flex min-h-11 w-full flex-col justify-center text-left desk:min-h-9"
          >
            <span className="font-serif text-base break-words">{row.name}</span>
            <span className="text-xs break-words text-navy/65">{campaignWord(row)}</span>
            {row.growthCampaignId === null ? null : (
              <span className="text-xs text-navy/65">{FROM_GROWTH}</span>
            )}
            {elsewhere === null ? null : (
              <span className="mt-1 text-xs break-words text-navy/65">{elsewhere}</span>
            )}
          </button>
        );
      },
    },
    {
      key: 'days',
      header: 'Days',
      width: '16%',
      cellClassName: 'text-navy/75',
      cell: (row) => shortDays(row),
    },
    {
      key: 'status',
      header: 'Status',
      width: '13%',
      minWidth: '80px',
      cell: (row) => (
        <Pill tone={statusTone(row.status)}>{CAMPAIGN_STATUS_LABELS[row.status]}</Pill>
      ),
    },
    {
      key: 'spent',
      header: 'Spent',
      width: '15%',
      cell: (row) => (
        <>
          <span className="block font-semibold">{money(row.spendCents)}</span>
          <span className="block text-xs text-navy/60">of {money(row.plannedBudgetCents)}</span>
        </>
      ),
    },
    { key: 'leads', header: 'Leads', width: '9%', cell: (row) => String(row.leads) },
    { key: 'households', header: 'Homes', width: '10%', cell: (row) => String(row.households) },
    {
      key: 'per-household',
      header: 'Each',
      width: '13%',
      cellClassName: 'font-semibold',
      cell: (row) => perHouseholdWords(row.costPerHouseholdCents),
    },
  ];
}

function CampaignCard({
  line,
  opened,
  onOpen,
}: {
  line: CampaignLine;
  opened: boolean;
  onOpen: () => void;
}) {
  return (
    <button type="button" onClick={onOpen} className="block w-full text-left">
      <Card className={opened ? 'border-gold' : ''}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-serif text-lg leading-tight">{line.name}</h2>
          <Pill tone={statusTone(line.status)}>{CAMPAIGN_STATUS_LABELS[line.status]}</Pill>
          {line.growthCampaignId === null ? null : <Pill>{FROM_GROWTH}</Pill>}
        </div>
        <p className="mt-1 text-sm text-navy/75">
          {CHANNEL_LABELS[line.channel]}, tagged {line.sourceDetail}
          {line.platform === null ? '' : `, on ${line.platform}`}.
        </p>
        <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm">
          <span>
            {money(line.spendCents)} spent of {money(line.plannedBudgetCents)}
          </span>
          <span className="font-semibold">
            {line.costPerHouseholdCents === null
              ? 'No household yet'
              : `${money(line.costPerHouseholdCents)} a household`}
          </span>
        </div>
        <p className="mt-1 text-xs text-navy/60">
          {plural(line.leads, 'lead', 'leads')},{' '}
          {plural(line.households, 'household', 'households')}. {countedWords(line.counted)}
        </p>
        <p className="mt-1 text-xs text-navy/60">{outcomeWords(line)}</p>
        {elsewhereWords(line) === null ? null : (
          <p className="mt-1 text-xs text-navy/60">{elsewhereWords(line)}</p>
        )}
      </Card>
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Typing one in, and correcting one. The same nine boxes both times.
 * ------------------------------------------------------------------ */

function AddOne({ today, onSaved }: { today: string; onSaved: (sentence: string) => void }) {
  const [typed, setTyped] = useState<CampaignTyped>(() => emptyCampaign(today));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    const answer = checkCampaign(typed);
    if (!answer.ok) {
      setProblem(answer.problem);
      return;
    }
    setSaving(true);
    try {
      await createCampaign(answer.body);
      setTyped(emptyCampaign(today));
      onSaved('That campaign is recorded.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That campaign was not recorded.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <details className="mb-5 rounded-xl border border-navy/12 bg-parchment p-4">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-lg">
        Add a campaign
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not recorded" message={problem} />
          </div>
        ) : null}
        <CampaignFields idPrefix="new-campaign" typed={typed} onChange={setTyped} />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Recording' : 'Record this campaign'}
        </Button>
      </form>
    </details>
  );
}

/** The nine boxes, written once, so the form and the correction cannot say different things. */
function CampaignFields({
  idPrefix,
  typed,
  onChange,
}: {
  idPrefix: string;
  typed: CampaignTyped;
  onChange: (next: CampaignTyped) => void;
}) {
  const field = (name: string) => `${idPrefix}-${name}`;
  const set = <Key extends keyof CampaignTyped>(key: Key, value: CampaignTyped[Key]) =>
    onChange({ ...typed, [key]: value });

  return (
    <>
      <TextField
        id={field('name')}
        label="Name"
        required
        value={typed.name}
        onChange={(value) => set('name', value)}
        autoComplete="off"
        hint='What the office calls it. "Letters to Lansbrook, first 500" reads better in a year than "letters".'
      />
      <SelectField
        id={field('channel')}
        label="Channel it buys"
        required
        value={typed.channel}
        onChange={(value) => set('channel', value)}
        options={CHANNELS}
        toLabel={(value) => CHANNEL_LABELS[value]}
        placeholder="Choose the channel"
        hint="The channel its enquiries and households arrive on."
      />
      <TextField
        id={field('word')}
        label="The word its enquiries carry"
        required
        value={typed.sourceDetail}
        onChange={(value) => set('sourceDetail', value)}
        autoComplete="off"
        placeholder="letter/2026-11"
        hint="The campaign word on its links and QR codes, or exactly the source detail typed on its enquiries. Households are counted by this word on this channel, so a word no visit carries and nobody types counts nobody."
      />
      <TextField
        id={field('platform')}
        label="Where it runs"
        value={typed.platform}
        onChange={(value) => set('platform', value)}
        autoComplete="off"
        hint="Optional. The print shop, Nextdoor, Meta."
      />
      <TextField
        id={field('budget')}
        label="Planned budget in dollars"
        required
        value={typed.dollars}
        onChange={(value) => set('dollars', value)}
        inputMode="numeric"
        autoComplete="off"
        placeholder="450.00"
        hint="What it was meant to cost. 0 is an answer, for a batch paid for in time rather than money. The spend against it is what the expenses naming it add up to."
      />
      <TextField
        id={field('starts')}
        label="First day"
        type="date"
        required
        value={typed.startsOn}
        onChange={(value) => set('startsOn', value)}
        hint="Enquiries and households count from the start of this day, in Florida."
      />
      <TextField
        id={field('ends')}
        label="Last day"
        type="date"
        value={typed.endsOn}
        onChange={(value) => set('endsOn', value)}
        hint="Optional. Leave it empty and it counts through today. With a last day it counts fourteen days past it, for the visitor who booked a fortnight after they saw it."
      />
      <SelectField
        id={field('status')}
        label="Status"
        required
        value={typed.status}
        onChange={(value) => set('status', value)}
        options={CAMPAIGN_STATUSES}
        toLabel={(value) => CAMPAIGN_STATUS_LABELS[value]}
        hint="What the office says about it. The counting goes by the days, never by this."
      />
      <TextAreaField
        id={field('notes')}
        label="Anything to add"
        value={typed.notes}
        onChange={(value) => set('notes', value)}
        rows={2}
        hint="Optional. What it is expected to produce, or who it went to."
      />
    </>
  );
}

/** What a line growth filed says beside its name, in the list, on its card and on its panel. */
export const FROM_GROWTH = 'From growth';

/** What the panel of a campaign growth filed says above its one box. */
export const GROWTH_CAMPAIGN_WORDS =
  'From growth. Growth sends this campaign again whenever it changes, so only its notes are typed here.';

/** One campaign, opened: the office's in full, or growth's on its notes alone. */
function OpenedCampaign(props: {
  line: CampaignLine;
  onClose: () => void;
  onSaved: (sentence: string) => void;
}) {
  return props.line.growthCampaignId === null ? (
    <CampaignPanel {...props} />
  ) : (
    <GrowthCampaignPanel {...props} />
  );
}

/**
 * A CAMPAIGN GROWTH FILED, OPENED: its figures, what it is as growth sent it, and its notes, the one
 * field the office keeps on it. No discard: a growth campaign ends by growth sending `ended`, and
 * its word stays held and its spend counted.
 */
function GrowthCampaignPanel({
  line,
  onClose,
  onSaved,
}: {
  line: CampaignLine;
  onClose: () => void;
  onSaved: (sentence: string) => void;
}) {
  const [notes, setNotes] = useState(line.notes ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setSaving(true);
    try {
      await patchCampaign(line.id, { notes: orNull(notes) });
      onSaved('Those notes are on the record, with what they said before.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'Those notes were not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SidePanel
      title={line.name}
      pills={
        <>
          <Pill tone={statusTone(line.status)}>{CAMPAIGN_STATUS_LABELS[line.status]}</Pill>
          <Pill>{FROM_GROWTH}</Pill>
        </>
      }
      onClose={onClose}
    >
      <p className="text-sm text-navy/75">
        {plural(line.leads, 'lead', 'leads')} and{' '}
        {plural(line.households, 'household', 'households')} for {money(line.spendCents)}.{' '}
        {countedWords(line.counted)}
        {elsewhereWords(line) === null ? null : ` ${elsewhereWords(line)}`}
      </p>
      <p className="text-sm text-navy/75">{outcomeWords(line)}</p>
      <p className="text-sm text-navy/75">
        {campaignWord(line)}
        {line.platform === null ? '' : `, on ${line.platform}`}. {shortDays(line)}, planned at{' '}
        {money(line.plannedBudgetCents)}.
      </p>
      <p className={`text-sm text-navy/75 ${MEASURE}`}>{GROWTH_CAMPAIGN_WORDS}</p>

      <form onSubmit={save} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <TextAreaField
          id={`campaign-${line.id}-notes`}
          label="Notes"
          value={notes}
          onChange={setNotes}
          rows={3}
          hint="The office's own words on this campaign. Growth never writes over them."
        />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Save the notes'}
        </Button>
      </form>
    </SidePanel>
  );
}

/**
 * One campaign, opened. A correction first, and the quiet discard under everything worth doing
 * before it. The spend that named a discarded campaign keeps naming it, because the money was
 * spent on it either way, and the hint says so.
 */
function CampaignPanel({
  line,
  onClose,
  onSaved,
}: {
  line: CampaignLine;
  onClose: () => void;
  onSaved: (sentence: string) => void;
}) {
  const [typed, setTyped] = useState<CampaignTyped>(() => typedFrom(line));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    const answer = checkCampaign(typed);
    if (!answer.ok) {
      setProblem(answer.problem);
      return;
    }
    setSaving(true);
    try {
      await patchCampaign(line.id, answer.body);
      onSaved('That correction is on the record, with what it said before.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That correction was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <SidePanel
      title={line.name}
      pills={<Pill tone={statusTone(line.status)}>{CAMPAIGN_STATUS_LABELS[line.status]}</Pill>}
      onClose={onClose}
    >
      <p className="text-sm text-navy/75">
        {plural(line.leads, 'lead', 'leads')} and{' '}
        {plural(line.households, 'household', 'households')} for {money(line.spendCents)}.{' '}
        {countedWords(line.counted)}
        {elsewhereWords(line) === null ? null : ` ${elsewhereWords(line)}`}
      </p>
      <p className="text-sm text-navy/75">{outcomeWords(line)}</p>

      <form onSubmit={save} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not saved" message={problem} />
          </div>
        ) : null}
        <CampaignFields idPrefix={`campaign-${line.id}`} typed={typed} onChange={setTyped} />
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Save this correction'}
        </Button>
      </form>

      <PanelRule />
      <DiscardControl
        idPrefix={`campaign-${line.id}`}
        what="this campaign"
        doneSentence="Discarded. It is off the register, and why is on the record."
        hint="It leaves the register and its figures stop showing. The spend naming it keeps its name, because the money was spent either way. The record and the reason stay."
        onDiscard={(body: Discard) => discardCampaign(line.id, body)}
        onDone={onSaved}
      />
    </SidePanel>
  );
}
